// Named column-mapping profiles, keyed by header signature (Phase 3, spec §9).
//
// When a user confirms a column→role mapping for an arbitrary tabular CSV, we
// save it keyed by the file's header signature so re-importing the same shape
// skips the mapping step. In the browser this persists to localStorage; the
// serialized shape matches the on-disk JSON the Python store writes
// (evals/adapters/_mapping_profiles.py) so a profile is portable.

import type { ColumnMapping } from "./_column_mapping";

const STORAGE_KEY = "aurora.csvMappingProfiles.v1";

export interface MappingProfile {
  name: string;
  mapping: ColumnMapping;
}

type Store = Record<string, MappingProfile>;

// Pluggable backing store so tests don't depend on a real localStorage and the
// caller can pass a per-case store if desired. Defaults to window.localStorage.
export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

function defaultStore(): KeyValueStore | null {
  try {
    if (typeof localStorage !== "undefined") return localStorage;
  } catch {
    /* localStorage may throw in some sandboxes */
  }
  return null;
}

function readAll(store: KeyValueStore | null): Store {
  if (!store) return {};
  try {
    const raw = store.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Store) : {};
  } catch {
    return {};
  }
}

/** Persist `mapping` under `signature` (overwriting any prior entry). */
export function saveProfile(
  signature: string,
  mapping: ColumnMapping,
  name = "",
  store: KeyValueStore | null = defaultStore(),
): void {
  if (!store) return;
  const all = readAll(store);
  all[signature] = { name, mapping };
  store.setItem(STORAGE_KEY, JSON.stringify(all));
}

/** Return the saved profile for `signature`, or null if none. */
export function loadProfile(
  signature: string,
  store: KeyValueStore | null = defaultStore(),
): MappingProfile | null {
  return readAll(store)[signature] ?? null;
}
